/**
 * Extract a human-readable name from an AWS ARN.
 *
 * For STS assumed-role ARNs like:
 *   arn:aws:sts::123456789012:assumed-role/RoleName/user@amazon.com
 * Returns: "RoleName / user@amazon.com"
 *
 * For other ARN formats, returns the resource portion after the last colon or slash.
 * For non-ARN strings, returns the input unchanged.
 */
export function humanizeArn(arn: string): string {
  if (!arn || !arn.startsWith('arn:')) {
    return arn || '';
  }

  // STS assumed-role: arn:aws:sts::ACCOUNT:assumed-role/ROLE/SESSION
  const assumedRoleMatch = arn.match(
    /^arn:aws:sts::[^:]*:assumed-role\/([^/]+)\/(.+)$/
  );
  if (assumedRoleMatch) {
    const [, roleName, sessionName] = assumedRoleMatch;
    return `${roleName} / ${sessionName}`;
  }

  // Generic ARN: return the resource portion (after last colon or slash)
  const parts = arn.split(':');
  const resource = parts[parts.length - 1];
  const segments = resource.split('/');
  return segments[segments.length - 1] || arn;
}

/**
 * Check if a string looks like an AWS ARN.
 */
export function isArn(value: string): boolean {
  return !!value && value.startsWith('arn:');
}
